/**
 * @file Compliance.jsx
 * @description Compliance component for displaying and managing compliance rules.
 * @component Compliance
 * @exports Compliance - Main component for displaying and managing compliance rules.
 */

import React, { useEffect, useState, useMemo } from "react";
import { Edit, Visibility } from "@mui/icons-material";
import TextField from '@mui/material/TextField';
import IconButton from "@mui/material/IconButton";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FormControlLabel from '@mui/material/FormControlLabel';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Switch,
    Button
} from "@mui/material";
import { COMPLIANCE_STRINGS, COMPLIANCE_TABLE_COLUMNS, COMPLIANCE_TABLE_COLUMN_KEYS, COMPLIANCE_MESSAGES } from "../../../common/constants/adminModule/complianceConstants.js";
import { STRINGS, SIZES, VARIANTS, BUTTON_COLORS, AUTO_COMPLETE } from "../../../common/constants/commonConstants.js";
import { getComplianceRules, updateComplianceRules } from "../../../services/adminModule/compliance/complianceServices.js";
import DialogComponent from "../../../common/components/DialogComponent.jsx";
import ProgressOverlay from "../../../common/components/ProgressOverlay.jsx";

const Compliance = () => {
    const [complianceRules, setComplianceRules] = useState([]);
    const [selectedCompliance, setSelectedCompliance] = useState(null);
    const [editComplianceValue, setEditComplianceValue] = useState("");
    const [isComplianceSaving, setIsComplianceSaving] = useState(false);
    const [isComplianceLoading, setIsComplianceLoading] = useState(false);
    const [editComplianceEnabled, setEditComplianceEnabled] = useState(false);
    const [isViewComplianceModalOpen, setIsViewComplianceModalOpen] = useState(false);
    const [isEditComplianceModalOpen, setIsEditComplianceModalOpen] = useState(false);

    useEffect(() => {
        loadComplianceRules();
    }, []);

    const loadComplianceRules = async () => {
        setIsComplianceLoading(true);
        try {
            const complianceData = await getComplianceRules();
            setComplianceRules(complianceData);
        } catch (error) {
            console.error("Error loading Compliance Rules", error);
        } finally {
            setIsComplianceLoading(false);
        }
    };

    const groupedComplianceRules = useMemo(() => {
        const complianceGroups = {};
        complianceRules.forEach((complianceRule) => {
            if (!complianceGroups[complianceRule.groupname]) complianceGroups[complianceRule.groupname] = [];
            complianceGroups[complianceRule.groupname].push(complianceRule);
        });
        return complianceGroups;
    }, [complianceRules]);

    const handleEditCompliance = (selectedCompliance) => {
        setSelectedCompliance(selectedCompliance);
        setEditComplianceValue(selectedCompliance.value);
        setEditComplianceEnabled(!!Number(selectedCompliance.IsEnabled));
        setIsEditComplianceModalOpen(true);
    };

    const handleViewCompliance = (selectedCompliance) => {
        setSelectedCompliance(selectedCompliance);
        setIsViewComplianceModalOpen(true);
    };

    const closeViewComplianceModal = () => {
        setIsViewComplianceModalOpen(false);
    };

    const closeEditComplianceModal = () => {
        setIsEditComplianceModalOpen(false);
    };

    const handleSaveCompliance = async () => {
        setIsComplianceSaving(true);
        setIsEditComplianceModalOpen(false);
        const payload = {
            Id: selectedCompliance.Id,
            value: editComplianceValue,
            IsEnabled: editComplianceEnabled ? 1 : 0
        };

        try {
            await updateComplianceRules(payload);
            await loadComplianceRules();
        } catch (error) {
            console.error("Error saving compliance:", error);
        } finally {
            setIsComplianceSaving(false);
        }
    };

    return (
        <>
            {(isComplianceSaving || isComplianceLoading) && <ProgressOverlay />}
            {!isComplianceLoading && complianceRules.length === 0 && (
                <Typography
                    align="center"
                    sx={{ mt: 3, mb: 3 }}
                >
                    {COMPLIANCE_MESSAGES.NO_RULES}
                </Typography>
            )}
            {Object.keys(groupedComplianceRules).map((groupKey) => (
                <Accordion key={groupKey}>
                    <AccordionSummary sx={{ flexDirection: "row-reverse" }} expandIcon={<ExpandMoreIcon />}>
                        <Typography sx={{ ml: 1 }}>{groupKey}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Table size={SIZES.SMALL}>
                            <TableHead>
                                <TableRow>
                                    {COMPLIANCE_TABLE_COLUMNS.map((column) => (
                                        <TableCell
                                            key={column.id}
                                            sx={{
                                                width:
                                                    column.id === COMPLIANCE_TABLE_COLUMN_KEYS.ACTIONS ? 75 :
                                                    column.id === COMPLIANCE_TABLE_COLUMN_KEYS.RULEID ? 75 :
                                                    column.id === COMPLIANCE_TABLE_COLUMN_KEYS.DESCRIPTION ? 500 :
                                                    column.id === COMPLIANCE_TABLE_COLUMN_KEYS.VALUE ? 75 : 
                                                    column.id === COMPLIANCE_TABLE_COLUMN_KEYS.ISENABLED ? 75 : 250,
                                            }}
                                        >
                                            {column.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {groupedComplianceRules[groupKey].length > 0 ? (
                                    groupedComplianceRules[groupKey].map((row) => (
                                        <TableRow key={row.Id}>
                                            <TableCell>
                                                <IconButton onClick={() => handleEditCompliance(row)}>
                                                    <Edit fontSize={SIZES.SMALL} />
                                                </IconButton>
                                                <IconButton onClick={() => handleViewCompliance(row)}>
                                                    <Visibility fontSize={SIZES.SMALL} />
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>{row.ruleID}</TableCell>
                                            <TableCell>{row.description}</TableCell>
                                            <TableCell>{row.value}</TableCell>
                                            <TableCell>
                                                <Switch
                                                    color={VARIANTS.PRIMARY}
                                                    checked={!!Number(row.IsEnabled)}
                                                    disabled
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            {COMPLIANCE_MESSAGES.NO_RULES}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </AccordionDetails>
                </Accordion>
            ))}
            <DialogComponent
                open={isViewComplianceModalOpen}
                setWidth="xs"
                onClose={closeViewComplianceModal}
                title={`${COMPLIANCE_STRINGS.RULE_DESCRIPTION} - ${selectedCompliance?.ruleID}`}
            >
                <p>{selectedCompliance?.ruleDescription}</p>
            </DialogComponent>
            <DialogComponent
                open={isEditComplianceModalOpen}
                setWidth="xs"
                onClose={closeEditComplianceModal}
                title={`${COMPLIANCE_STRINGS.EDIT_RULE} - ${selectedCompliance?.ruleID}`}
                actions={
                    <Button
                        variant={VARIANTS.CONTAINED}
                        color={BUTTON_COLORS.PRIMARY}
                        onClick={handleSaveCompliance}
                    >
                        {STRINGS.SAVE}
                    </Button>
                }
            >
                <TextField
                    fullWidth
                    size={SIZES.SMALL}
                    value={editComplianceValue}
                    variant={VARIANTS.OUTLINED}
                    label={COMPLIANCE_STRINGS.VALUE}
                    autoComplete={AUTO_COMPLETE.OFF}
                    onChange={(e) => setEditComplianceValue(e.target.value)}
                />
                <FormControlLabel
                    value="end"
                    control={<Switch
                        checked={editComplianceEnabled}
                        onChange={(e) =>
                            setEditComplianceEnabled(e.target.checked)
                        }
                    />}
                    label="Enabled"
                    labelPlacement="start"
                    sx={{
                        ml: 0,
                        mt: "15px",
                    }}
                />
            </DialogComponent>
        </>
    );
};

export default Compliance;